import React, { useState, useEffect } from 'react';
import { Group, Image, Rect } from 'react-konva';
import Score from './Score';
import {
  GetScoreDefn,
  getScoreText,
  GetTextColor,
  isNullOrUndefined,
} from 'utils/Global';
import EAutoScoreType from 'enums/EAutoScoreType';
import EColors from 'enums/EColors';
import {
  getScoreToolTip,
  getScoreSizes,
  computeShiftsAndTotalShift,
  computeXPositions,
  findHoveredScoreIndex,
  sortScoreItems,
} from './utils';
import { RMSvgIconImage } from 'components/customComponents/CommonComponents/RMSvgIcon';
import { UpdateGlobalDataStoreVersion } from 'business/services/GlobalService';
import {
  ALLSCORESGROUPSSTORE,
  MANUALSCOREGROUPSSTORE,
  SCOREDEFNSTORE,
} from 'utils/IndexDBGlobal';
import { waitForValue } from 'components/commandpanel/ImageTuning/utils';
import { useIsUpdatingGlobalData } from 'redux/hooks/experiment';
import { useSystemProperty } from 'CustomHooks/useSystemProperty';
import { PropertyConstants } from 'enums/PropertyConstants';
import { ScoresOfWellDropsDTO } from '../../../business/models/Models';
import {
  DEFAULT_WELL_SCORE_EXPANDED_SIZE,
  DEFAULT_WELL_SCORE_HOVERED_SIZE,
} from '../../../constants/scoreConstants';

const disputeIcon = RMSvgIconImage({ name: 'DisputeScoreIcon' });
const ignoredAutoScoreIcon = RMSvgIconImage({ name: 'EyeSlash', fill: '#dc0100' });

interface ScoresProps {
  enableHoverZoom?: boolean;
  hoverBaseSize?: number;
  hoveredSize?: number;
  onRowHeightChange?: (height: number) => void;
}

export default function Scores(props: ScoresProps | any) {
  const {
    enableHoverZoom,
    hoverBaseSize = DEFAULT_WELL_SCORE_EXPANDED_SIZE,
    hoveredSize = DEFAULT_WELL_SCORE_HOVERED_SIZE,
    onRowHeightChange,
  } = props;
  const [scoreItems, setScoreItems] = useState<any[]>([]);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const { updatingGlobalData, setUpdatingGlobalData } = useIsUpdatingGlobalData();
  const AllowDisputedScoreDrawing = useSystemProperty(
    PropertyConstants.AllowDisputedScoreDrawing,
    false,
  );
  useEffect(() => {
    const fetchScores = async () => {
      const items = await getScoreItems();
      setScoreItems(items);
      setHoveredIndex(null);
    };

    fetchScores();
  }, [props.scoresOfWellDrops, AllowDisputedScoreDrawing]);

  useEffect(() => {
    if (!onRowHeightChange) {
      return;
    }
    onRowHeightChange(
      enableHoverZoom && hoveredIndex !== null ? hoveredSize : props.height,
    );
  }, [enableHoverZoom, hoveredIndex, hoveredSize, props.height, onRowHeightChange]);

  async function getScoreItems() {
    if (isNullOrUndefined(GetScoreDefn())) {
      return [];
    }
    const items = [];
    const isDisputed = props.scoresOfWellDrops?.some((el) => el.isDisputed === true);
    const disputedOffset = isDisputed && AllowDisputedScoreDrawing;

    for (let i = 0; i < props.scoresOfWellDrops?.length; i++) {
      const wellDropScore = props.scoresOfWellDrops[i];
      if (wellDropScore.isUVIndicator) {
        items.push({
          key: `score-${i}`,
          isUVIndicator: true,
          isAutoScore: wellDropScore.isAutoScore,
          sortName: wellDropScore.scoreGroupName,
          color: `rgb(${wellDropScore.colorRGB})`,
          textColor: GetTextColor(wellDropScore.colorRGB),
          scoreText: wellDropScore.scoreName,
          isDisputedItem: false,
          scoreType: wellDropScore.scoreType,
          toolTipText: getScoreToolTip(wellDropScore, null),
          isAutoScoreIgnored: wellDropScore.isAutoScoreIgnored,
        });
        continue;
      }
      let score = GetScoreDefn().find(
        (score) => score.wellDropScoreId === wellDropScore.wellDropScoreId,
      );
      if (!score) {
        if (!updatingGlobalData) {
          setUpdatingGlobalData(true);
          // await for score definition gdata to update version
          await UpdateGlobalDataStoreVersion([SCOREDEFNSTORE.name]);
          // fire and forget for remaining scores related gdata
          UpdateGlobalDataStoreVersion([
            ALLSCORESGROUPSSTORE.name,
            MANUALSCOREGROUPSSTORE.name,
          ]).finally(() => {
            setUpdatingGlobalData(false);
          });
        }

        score = await waitForValue({
          action: () => {
            return GetScoreDefn().find(
              (score) => score.wellDropScoreId === wellDropScore.wellDropScoreId,
            );
          },
        });
      }

      items.push({
        key: `score-${i}`,
        isUVIndicator: false,
        isAutoScore: score.isAutoScore,
        sortName: score.scoreGroupName,
        color: `rgb(${score.color})`,
        textColor:
          wellDropScore.autoScoreType === EAutoScoreType.IP
            ? EColors.White
            : GetTextColor(score.color),
        scoreText: getScoreText(wellDropScore, score),
        isDisputedItem: wellDropScore.isDisputed,
        scoreType: wellDropScore.scoreType,
        toolTipText: getScoreToolTip(wellDropScore, score),
        isAutoScoreIgnored: wellDropScore.isAutoScoreIgnored,
      });
    }

    const sortedItems = sortScoreItems(items);

    sortedItems.forEach((item, index) => {
      item.column = index + (item.isUVIndicator ? 0 : disputedOffset ? 1 : 0);
    });

    return sortedItems;
  }

  const isDisputed = props.scoresOfWellDrops?.some(
    (score: ScoresOfWellDropsDTO) => score.isDisputed === true,
  );
  const showDisputeIcon = isDisputed && AllowDisputedScoreDrawing;
  const baseSize = props.width;
  const scale = props.scale ? props.scale : 1;

  const n = scoreItems.length;
  const sizePositions = getScoreSizes({
    count: n,
    hoveredIndex,
    enableHoverZoom,
    baseSize,
    hoverBaseSize,
    hoveredSize,
  });
  const { shiftForIndex, totalShift } = computeShiftsAndTotalShift(
    sizePositions,
    baseSize,
  );
  const xPositions = computeXPositions(
    scoreItems,
    sizePositions,
    shiftForIndex,
    props.x,
    baseSize,
  );

  const scoreComponents = scoreItems.map((item, i) => (
    <Group
      id={`scores-img-component-${i}`}
      key={item.key}
      onMouseEnter={enableHoverZoom ? () => setHoveredIndex(i) : undefined}
    >
      <Score
        id={`scores-${i}`}
        x={xPositions[i]}
        y={props.y}
        width={sizePositions[i]}
        height={sizePositions[i]}
        color={item.color}
        scale={scale}
        Scores={item.scoreText}
        textcolor={item.textColor}
        isDisputed={item.isDisputedItem}
        scoreType={item.scoreType}
        toolTipText={item.toolTipText}
        sizeDetails={props.sizeDetails}
      />
      {item.isAutoScoreIgnored && (
        <Image
          id={`scores-ignored-autoscore-icon-${i}`}
          x={xPositions[i] / scale}
          y={props.y / scale}
          width={sizePositions[i] / scale}
          height={sizePositions[i] / scale}
          image={ignoredAutoScoreIcon}
        />
      )}
    </Group>
  ));

  return (
    <Group onMouseLeave={enableHoverZoom ? () => setHoveredIndex(null) : undefined}>
      {enableHoverZoom && n > 0 && (
        <Rect
          id="scores-hover-backdrop"
          x={props.x - totalShift - baseSize}
          y={props.y}
          width={baseSize * (n + 2) + totalShift}
          height={hoveredSize}
          fill="transparent"
          onMouseMove={(e) => {
            const pos = e.target.getRelativePointerPosition();
            setHoveredIndex(
              pos
                ? findHoveredScoreIndex(pos, scoreItems, {
                    hoveredIndex,
                    xPositions,
                    sizePositions,
                    originX: props.x,
                    originY: props.y,
                    baseSize,
                  })
                : null,
            );
          }}
        />
      )}
      {showDisputeIcon && (
        <Image
          id="scores-img-component"
          x={props.x - totalShift}
          y={props.y}
          width={baseSize}
          height={baseSize}
          image={disputeIcon}
        />
      )}
      {scoreComponents}
    </Group>
  );
}
